import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useGetInsuranceCarriersQuery } from '@/api/hooks/queries/use-get-insurance-carriers-query';
import { GeneralErrorIndicator } from '@/components/GeneralErrorIndicator.tsx';
import { Button } from '@/components/ui/button.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox.tsx';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import type { stateNames } from '@/lib/zipcode';
import {
  CARRIER_TYPE,
  type InsuranceCarrier,
  PLAN_FAMILY,
  type PlanFamily,
  type QueuedInsurance,
  type UnprovidedInsurance,
  type UnverifiedInsurance,
  groupByType,
} from '@/types/insurance.type.ts';
import { FoundCoverageBanner } from '@/views/insurance/flows/eligibility-revamp/components/FoundCoverageBanner.tsx';
import { ProgressiveQuestionReveal } from '@/views/insurance/flows/eligibility-revamp/components/ProgressiveQuestionReveal.tsx';

const medicareSelectionSchema = z.enum(['none', 'base', 'managed']);
const medicaidHasCoverageSchema = z.enum(['yes', 'no']);
const medicaidIsManagedSchema = z.enum(['yes', 'no']);

type MedicareSelection = z.infer<typeof medicareSelectionSchema>;
type MedicaidIsManaged = z.infer<typeof medicaidIsManagedSchema>;

interface AskAllFormProps {
  family: PlanFamily;
  state: keyof typeof stateNames | undefined;
  foundCoverageLabel?: string;
  showError?: boolean;
  initialInsurance?: UnverifiedInsurance;
  onNoCoverage: () => void;
  onSubmit: (insurance: QueuedInsurance) => void;
  onCannotProvide: (insurance: UnprovidedInsurance) => void;
}

const formId = 'ask-all-form';
const hiddenManagedPlanNames = new Set(['Medicaid', 'Medicare']);

const formSchema = z.object({
  medicareSelection: medicareSelectionSchema.optional(),
  medicaidHasCoverage: medicaidHasCoverageSchema.optional(),
  medicaidIsManaged: medicaidIsManagedSchema.optional(),
  internalId: z.string().optional(),
  memberId: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export function AskAllForm({
  family,
  state,
  foundCoverageLabel,
  showError,
  initialInsurance,
  onNoCoverage,
  onSubmit,
  onCannotProvide,
}: AskAllFormProps) {
  const isMedicare = family === PLAN_FAMILY.MEDICARE;
  const baseType = isMedicare ? CARRIER_TYPE.MEDICARE : CARRIER_TYPE.MEDICAID;
  const managedType = isMedicare
    ? CARRIER_TYPE.MEDICARE_ADVANTAGE
    : CARRIER_TYPE.MEDICAID_MANAGED_CARE;

  let initialSelection: MedicareSelection | undefined;
  if (initialInsurance?.carrier.carrierType === baseType) {
    initialSelection = 'base';
  } else if (initialInsurance?.carrier.carrierType === managedType) {
    initialSelection = 'managed';
  }

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      medicareSelection: isMedicare ? initialSelection : undefined,
      medicaidHasCoverage: !isMedicare && initialInsurance ? 'yes' : undefined,
      medicaidIsManaged: !isMedicare
        ? getInitialMedicaidManagedSelection(initialSelection)
        : undefined,
      memberId: initialInsurance?.memberId ?? '',
      internalId: initialInsurance?.carrier.internalId ?? '',
    },
  });
  const [medicareSelection, medicaidHasCoverage, medicaidIsManaged] = useWatch({
    control: form.control,
    name: ['medicareSelection', 'medicaidHasCoverage', 'medicaidIsManaged'],
  });

  // Derived selection for validation/submission
  const selection = ((): MedicareSelection | null => {
    if (isMedicare) {
      return medicareSelection ?? null;
    }
    if (medicaidHasCoverage === 'no') {
      return 'none';
    }
    if (medicaidHasCoverage === 'yes' && medicaidIsManaged === 'no') {
      return 'base';
    }
    if (medicaidHasCoverage === 'yes' && medicaidIsManaged === 'yes') {
      return 'managed';
    }
    return null;
  })();

  const {
    data: carriers = [],
    isLoading,
    isSuccess,
    isError,
  } = useGetInsuranceCarriersQuery(state);

  const carriersByType = useMemo(() => groupByType(carriers), [carriers]);
  const managedPlanCarriers = useMemo(
    () =>
      (carriersByType.get(managedType) ?? [])
        .filter((carrier) => !hiddenManagedPlanNames.has(carrier.name))
        .sort((a, b) => Number(a.isOther) - Number(b.isOther)),
    [carriersByType, managedType]
  );

  const baseCarrierName = isMedicare ? 'Medicare' : 'Medicaid';
  const baseCarrier = useMemo(
    () => carriersByType.get(baseType)?.find((c) => c.name === baseCarrierName) ?? null,
    [carriersByType, baseType, baseCarrierName]
  );

  const getSelectedManagedPlanCarrier = (internalId: string) =>
    managedPlanCarriers.find((c) => c.internalId === internalId);

  const validateAndSubmit = () => {
    if (isMedicare && !medicareSelection) {
      form.setError('medicareSelection', { message: 'Please select whether you have Medicare.' });
      return;
    }

    if (!isMedicare && !medicaidHasCoverage) {
      form.setError('medicaidHasCoverage', { message: 'Please select whether you have Medicaid.' });
      return;
    }

    if (!isMedicare && medicaidHasCoverage === 'yes' && !medicaidIsManaged) {
      form.setError('medicaidIsManaged', {
        message: 'Please select whether your Medicaid plan is managed.',
      });
      return;
    }

    if (selection === 'none') {
      onNoCoverage();
      return;
    }

    const data = form.getValues();

    if (selection === 'base') {
      if (!data.memberId) {
        form.setError('memberId', { message: 'Member ID is required' });
        return;
      }
      if (!baseCarrier) {
        throw new Error(`Base carrier "${baseCarrierName}" not found in carrier list`);
      }
      onSubmit({ carrier: baseCarrier, memberId: data.memberId });
      return;
    }

    if (selection === 'managed') {
      let hasError = false;
      if (!data.internalId) {
        form.setError('internalId', { message: 'Please select a plan' });
        hasError = true;
      }
      if (!data.memberId) {
        form.setError('memberId', { message: 'Member ID is required' });
        hasError = true;
      }
      if (hasError) return;

      const carrier = getSelectedManagedPlanCarrier(data.internalId!);
      if (!carrier) throw new Error("Selected carrier that doesn't exist.");
      onSubmit({ carrier, memberId: data.memberId! });
    }
  };

  const handleCannotProvide = () => {
    const currentType = selection === 'base' ? baseType : managedType;
    const data = form.getValues();
    onCannotProvide({
      carrierType: currentType,
      carrier:
        selection === 'base'
          ? (baseCarrier ?? undefined)
          : getSelectedManagedPlanCarrier(data.internalId ?? ''),
      memberId: data.memberId ?? '',
    });
  };

  const title = isMedicare
    ? "Let's first check for Medicare coverage"
    : "Now let's check for Medicaid coverage";

  const showMemberIdOnly = selection === 'base';
  const showCarrierAndMemberId = selection === 'managed';
  const showInputFields = showMemberIdOnly || showCarrierAndMemberId;
  const plansNotReady = !isSuccess || !state;

  return (
    <>
      <CardHeader>
        {foundCoverageLabel && <FoundCoverageBanner>{foundCoverageLabel}</FoundCoverageBanner>}
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {showError && (
          <div className="rounded-md bg-rose-50 p-4 text-sm text-rose-700">
            We couldn't verify your insurance. Please check your information and try again.
          </div>
        )}

        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            validateAndSubmit();
          }}
          id={formId}
        >
          <FieldGroup className="space-y-2">
            {/* Medicare: single question with 3 options */}
            {isMedicare && (
              <FieldSet>
                <FieldLegend className="text-charcoal-700 mb-4 text-base">
                  Do you have Medicare?
                </FieldLegend>
                <Controller
                  name="medicareSelection"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <>
                      <RadioGroup
                        value={field.value ?? ''}
                        onValueChange={(value) => {
                          field.onChange(medicareSelectionSchema.parse(value));
                          form.clearErrors(field.name);
                        }}
                        className="flex flex-col gap-4"
                      >
                        <Field orientation="horizontal" className="flex items-center gap-2">
                          <RadioGroupItem value="none" id={`${formId}-none`} />
                          <FieldLabel htmlFor={`${formId}-none`}>No</FieldLabel>
                        </Field>

                        <Field orientation="horizontal" className="flex items-center gap-2">
                          <RadioGroupItem value="base" id={`${formId}-base`} />
                          <FieldLabel htmlFor={`${formId}-base`}>
                            Yes, Original Medicare (Part A & B)
                          </FieldLabel>
                        </Field>

                        <Field orientation="horizontal" className="flex items-center gap-2">
                          <RadioGroupItem value="managed" id={`${formId}-managed`} />
                          <FieldLabel htmlFor={`${formId}-managed`}>
                            Yes, Medicare Advantage
                          </FieldLabel>
                        </Field>
                      </RadioGroup>
                      <FieldError errors={[fieldState.error]} />
                    </>
                  )}
                />
              </FieldSet>
            )}

            {/* Medicaid: two-step progressive disclosure */}
            {!isMedicare && (
              <>
                <FieldSet>
                  <FieldLegend className="text-charcoal-700 mb-4 text-base">
                    Do you have Medicaid?
                  </FieldLegend>
                  <Controller
                    name="medicaidHasCoverage"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <>
                        <RadioGroup
                          value={field.value ?? ''}
                          onValueChange={(value) => {
                            const hasCoverage = medicaidHasCoverageSchema.parse(value);
                            field.onChange(hasCoverage);
                            form.clearErrors(field.name);
                            if (hasCoverage === 'no') {
                              form.setValue('medicaidIsManaged', undefined);
                              form.clearErrors('medicaidIsManaged');
                            }
                          }}
                          className="flex flex-col gap-4"
                        >
                          <Field orientation="horizontal" className="flex items-center gap-2">
                            <RadioGroupItem value="yes" id={`${formId}-has-yes`} />
                            <FieldLabel htmlFor={`${formId}-has-yes`}>Yes</FieldLabel>
                          </Field>

                          <Field orientation="horizontal" className="flex items-center gap-2">
                            <RadioGroupItem value="no" id={`${formId}-has-no`} />
                            <FieldLabel htmlFor={`${formId}-has-no`}>No</FieldLabel>
                          </Field>
                        </RadioGroup>
                        <FieldError errors={[fieldState.error]} />
                      </>
                    )}
                  />
                </FieldSet>

                {medicaidHasCoverage === 'yes' && (
                  <ProgressiveQuestionReveal>
                    <FieldSet>
                      <FieldLegend className="text-charcoal-700 mb-4 text-base">
                        Is your Medicaid plan managed by another organization?{' '}
                        <span className="font-normal text-charcoal-500">
                          (e.g. Fidelis, United Healthcare, Humana)
                        </span>
                      </FieldLegend>
                      <Controller
                        name="medicaidIsManaged"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <>
                            <RadioGroup
                              value={field.value ?? ''}
                              onValueChange={(value) => {
                                field.onChange(medicaidIsManagedSchema.parse(value));
                                form.clearErrors(field.name);
                              }}
                              className="flex flex-col gap-4"
                            >
                              <Field orientation="horizontal" className="flex items-center gap-2">
                                <RadioGroupItem value="yes" id={`${formId}-managed-yes`} />
                                <FieldLabel htmlFor={`${formId}-managed-yes`}>Yes</FieldLabel>
                              </Field>

                              <Field orientation="horizontal" className="flex items-center gap-2">
                                <RadioGroupItem value="no" id={`${formId}-managed-no`} />
                                <FieldLabel htmlFor={`${formId}-managed-no`}>No</FieldLabel>
                              </Field>
                            </RadioGroup>
                            <FieldError errors={[fieldState.error]} />
                          </>
                        )}
                      />
                    </FieldSet>
                  </ProgressiveQuestionReveal>
                )}
              </>
            )}

            {showCarrierAndMemberId && (
              <Controller
                name="internalId"
                control={form.control}
                render={({ field, fieldState }) => {
                  const selectedCarrier =
                    managedPlanCarriers.find((c) => c.internalId === field.value) ?? null;

                  return (
                    <ProgressiveQuestionReveal>
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={`${formId}-internalId`}>
                          {isMedicare ? 'Medicare Advantage Carrier' : 'Managed Medicaid Plan'}
                        </FieldLabel>
                        <Combobox
                          items={managedPlanCarriers}
                          itemToStringLabel={(carrier) =>
                            carrier.isOther ? carrier.name : carrier.displayName
                          }
                          value={selectedCarrier}
                          onValueChange={(carrier) => field.onChange(carrier?.internalId ?? '')}
                          disabled={plansNotReady}
                        >
                          <ComboboxInput
                            placeholder={isLoading ? 'Loading...' : 'Select a carrier...'}
                            id={`${formId}-internalId`}
                            aria-label="Plan name"
                            aria-invalid={fieldState.invalid}
                            aria-busy={isLoading}
                            disabled={plansNotReady}
                          />
                          <ComboboxContent>
                            <ComboboxEmpty>No carriers found matching that keyword</ComboboxEmpty>
                            <ComboboxList>
                              {(carrier: InsuranceCarrier) => (
                                <ComboboxItem key={carrier.internalId} value={carrier}>
                                  {carrier.name}
                                </ComboboxItem>
                              )}
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                        {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                      </Field>
                    </ProgressiveQuestionReveal>
                  );
                }}
              />
            )}

            {showInputFields && (
              <Controller
                name="memberId"
                control={form.control}
                render={({ field, fieldState }) => (
                  <ProgressiveQuestionReveal>
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor={`${formId}-memberId`}>
                        {getMemberIdLabel(selection, isMedicare)}
                      </FieldLabel>
                      <Input
                        {...field}
                        placeholder="(e.g., AA11111A)"
                        id={`${formId}-memberId`}
                        aria-invalid={fieldState.invalid}
                        autoComplete="off"
                      />
                      {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                    </Field>
                  </ProgressiveQuestionReveal>
                )}
              />
            )}
          </FieldGroup>

          {isError && (
            <GeneralErrorIndicator>
              Failed to load insurance carriers. Please try again.
            </GeneralErrorIndicator>
          )}

          <div className="mt-8 w-full space-y-8 justify-self-center sm:w-auto sm:space-y-4.5 sm:justify-self-start">
            <Button className="w-full sm:w-auto sm:min-w-[186px]" type="submit">
              Continue
            </Button>

            {showInputFields && (
              <Button
                className="justify-self-center sm:justify-self-start sm:px-0"
                onClick={handleCannotProvide}
                type="button"
                size="sm"
                variant="link"
              >
                {getCannotProvideLinkText(selection, isMedicare)}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </>
  );
}

function getMemberIdLabel(selection: MedicareSelection | null, isMedicare: boolean): string {
  if (selection === 'base') {
    return isMedicare ? 'Medicare ID' : 'Medicaid ID';
  }
  return 'Member ID';
}

function getCannotProvideLinkText(
  selection: MedicareSelection | null,
  isMedicare: boolean
): string {
  if (selection === 'base') {
    return isMedicare ? "I don't know my Medicare ID" : "I don't know my Medicaid ID";
  }
  return "I don't have my plan info";
}

function getInitialMedicaidManagedSelection(
  initialSelection: MedicareSelection | undefined
): MedicaidIsManaged | undefined {
  if (initialSelection === 'base') {
    return 'no';
  }
  if (initialSelection === 'managed') {
    return 'yes';
  }
  return undefined;
}
